import { useState, useEffect } from 'react';
import { useSuperQuery, useSuperMutation } from '@vadiun/react-hooks-legacy';
import { appUsersService } from '../services/appUsersService';
import { useParams } from 'react-router-dom';

export const useAppUser = () => {
  const AppUsersService = appUsersService();
  const params = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inputSearch, setInputSearch] = useState<string | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [sort, setSort] = useState<{ sortDir: string; sortBy: string }>({
    sortDir: 'DESC',
    sortBy: 'createdAt'
  });
  const [userType, setUserType] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(inputSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  const getAppUsers = useSuperQuery(
    () =>
      AppUsersService.getAppUsers(
        page,
        rowsPerPage,
        search ? encodeURIComponent(search) : null,
        sort.sortBy,
        sort.sortDir,
        userType
      ),
    {
      showSpinner: true
    },
    [page, rowsPerPage, search, sort, userType]
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPage(1);
    setRowsPerPage(newRowsPerPage);
  };

  const handleSearchChange = (value: string | null) => {
    setInputSearch(value || null);
  };

  const handleSortChange = (direction: string | null, sortBy: string) => {
    setPage(1);
    setSort({
      sortDir: direction === 'desc' ? 'DESC' : 'ASC',
      sortBy
    });
  };

  const handleUserTypeChange = (type: string | null) => {
    setPage(1);
    setUserType(type);
  };

  const addCorporateMutation = useSuperMutation(
    appUsersService().addCorporateAppUser,
    {
      onMutationFinish(state, arg) {
        getAppUsers.reload();
      },
      messages: {
        success: { message: 'El Usuario corporativo fue creado con exito' }
      }
    }
  );

  const addParticularMutation = useSuperMutation(
    appUsersService().addParticularAppUser,
    {
      onMutationFinish(state, arg) {
        getAppUsers.reload();
      },
      messages: {
        success: {
          message: 'El Usuario particular fue creado con exito'
        }
      }
    }
  );

  const changeActiveStatus = useSuperMutation(
    (userId: string, isActive: boolean, authorizedId: string | null) =>
      appUsersService().changeActiveStatus(userId, isActive, authorizedId),
    {
      showSpinner: true
    }
  );

  const getAppUserById = useSuperQuery(
    () => {
      if (!params.id) return Promise.resolve(null);
      return AppUsersService.getAppUserById(params.id);
    },
    {
      showSpinner: true
    }
  );

  const editAppUserCorporateMutation = useSuperMutation(
    AppUsersService.editCorporateAppUser,
    {
      onMutationFinish(state, arg) {
        getAppUsers.reload();
      },
      messages: {
        success: {
          message: 'El Usuario fue editado con exito'
        }
      }
    }
  );

  const editAppUserParticularMutation = useSuperMutation(
    AppUsersService.editParticularAppUser,
    {
      onMutationFinish(state, arg) {
        getAppUsers.reload();
      },
      messages: {
        success: {
          message: 'El Usuario fue editado con exito'
        }
      }
    }
  );

  const deleteAppUserMutation = useSuperMutation(
    AppUsersService.deleteAppUser,
    {
      onMutationFinish() {
        getAppUsers.reload();
      },
      messages: {
        success: {
          message: 'El Usuario fue eliminado con exito'
        }
      }
    }
  );

  
  const onDownloadExcel = useSuperMutation(
    (values: any) => appUsersService().downloadExcel(values),
    { showSpinner: true }
  );

  return {
    appUsers: getAppUsers.data?.docs || [],
    totalItems: getAppUsers.data?.totalDocs || 0,
    loading: getAppUsers.isLoading,
    error: getAppUsers.error,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleSortChange,
    handleUserTypeChange,
    userType,
    addCorporateMutation,
    loadingAddMutation: addCorporateMutation.isLoading,
    errorAddMutation: addCorporateMutation.error,
    addParticularMutation,
    loadingAddParticularMutation: addParticularMutation.isLoading,
    errorAddParticularMutation: addParticularMutation.error,
    changeActiveStatus,
    reload: getAppUsers.reload,

    appUser: getAppUserById.data,
    loadingAppUser: getAppUserById.isLoading,
    errorAppUser: getAppUserById.error,
    reloadAppUser: getAppUserById.reload,

    editAppUserCorporate: editAppUserCorporateMutation.mutate,
    loadingEditAppUser: editAppUserCorporateMutation.isLoading,
    errorEditAppUser: editAppUserCorporateMutation.error,

    editAppUserParticular: editAppUserParticularMutation.mutate,
    loadingEditAppUserParticular: editAppUserParticularMutation.isLoading,
    errorEditAppUserParticular: editAppUserParticularMutation.error,

    deleteAppUser: deleteAppUserMutation.mutate,
    loadingDeleteAppUser: deleteAppUserMutation.isLoading,
    onDownloadExcel,
  };
};
